import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useStore } from "../../../../stores/store";
import { observer } from "mobx-react-lite";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import FormLayout from '../../../Generic/FormLayout';
import FormInput from '../../../Generic/FormInput';
import FormCheckbox from '../../../Generic/FormCheckbox';
import { useSetMenu } from "../../../../hooks/useSetMenu";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import * as yup from "yup";

const Edit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { mainStore } = useStore();
  const axiosPrivate = useAxiosPrivate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  useSetMenu(mainStore.ResearchPublicationsAdminMenuType, mainStore.ResearchPublicationsAdminMenuNo);

  const [authors, setAuthors] = useState([""]);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  const schema = yup.object({
    title: yup.string().required(t('requiredField')).max(500, t('maxLength', { max: 500 })),
    year: yup.number().required(t('requiredField')).min(1900, t('minValue', { min: 1900 })),
    category: yup.string().required(t('requiredField')),
    publicationOrder: yup.number().required(t('requiredField')).min(1, t('minValue', { min: 1 })),
  });

  const { register, handleSubmit, formState: { errors }, setValue } = useForm({ resolver: yupResolver(schema) });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axiosPrivate.get(`/ResearchPublications/${id}`);
        const data = response.data;
        Object.keys(data).forEach(key => {
          if (key !== 'authors') {
            setValue(key, data[key]);
          }
        });
        if (data.authors && data.authors.length > 0) {
          setAuthors(data.authors);
        }
        setLoading(false);
      } catch (error) {
        setNotFound(true);
        setLoading(false);
      }
    };
    fetchData();
  }, [id, axiosPrivate, setValue]);

  const handleAddAuthor = () => {
    setAuthors([...authors, ""]);
  };

  const handleRemoveAuthor = (index) => {
    const newAuthors = authors.filter((_, i) => i !== index);
    setAuthors(newAuthors.length > 0 ? newAuthors : [""]);
  };

  const handleAuthorChange = (index, value) => {
    const newAuthors = [...authors];
    newAuthors[index] = value;
    setAuthors(newAuthors);
  };

  const updateData = async (data) => {
    try {
      const payload = {
        ...data,
        id: parseInt(id),
        authors: authors.filter(a => a.trim() !== ""),
      };
      await axiosPrivate.put(`/ResearchPublications/${id}`, payload);
      enqueueSnackbar(t("successUpdate"), { variant: "success" });
      navigate("/Admin/ResearchPublications");
    } catch (error) {
      enqueueSnackbar(t("errorUpdate"), { variant: "error" });
    }
  };

  const onSubmit = data => updateData(data);

  const goToList = () => {
    navigate("/Admin/ResearchPublications");
  };

  if (loading) {
    return <div className="text-center p-4">{t("loading")}...</div>;
  }

  if (notFound) {
    return (
      <div className="not-found-myDiv">
        <div className="not-found-container">
          <div className="not-found-content">
            <h1>{t("pageNotFoundTitle")}</h1>
            <p>{t("noDataWithIdMessage")}</p>
            <button onClick={goToList} className="btn btn-primary">
              {t("goTo") + " " + t("list") + " " + t("researchPublications")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <FormLayout title={t("researchPublicationEditTitle") || "Edit Research Publication"} onSubmit={handleSubmit(onSubmit)} onCancel={() => navigate(-1)}>
      <FormInput label="order" name="publicationOrder" type="number" register={register} errors={errors} colSize={2} />
      <FormInput label="year" name="year" type="number" register={register} errors={errors} colSize={2} />
      <FormInput label="category" name="category" register={register} errors={errors} colSize={4} />
      <FormInput label="title" name="title" register={register} errors={errors} colSize={12} />
      <FormInput label="venue" name="venue" register={register} errors={errors} colSize={8} />
      <FormInput label="venueDate" name="venueDate" register={register} errors={errors} colSize={4} />
      <FormInput label="doi" name="doi" register={register} errors={errors} colSize={6} />
      <FormInput label="url" name="url" register={register} errors={errors} colSize={6} />

      <div className="col-12 mb-3">
        <label className="form-label">{t("authors")}</label>
        {authors.map((author, index) => (
          <div key={index} className="input-group mb-2">
            <input
              type="text"
              className="form-control"
              value={author}
              onChange={(e) => handleAuthorChange(index, e.target.value)}
              placeholder={t("authorName")}
            />
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={() => handleRemoveAuthor(index)}
            >
              <i className="bi bi-trash"></i>
            </button>
          </div>
        ))}
        <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleAddAuthor}>
          <i className="bi bi-plus"></i> {t("addAuthor")}
        </button>
      </div>

      <FormInput label="descriptionEN" name="descriptionEN" register={register} errors={errors} colSize={12} isTextArea={true} />
      <FormInput label="descriptionBG" name="descriptionBG" register={register} errors={errors} colSize={12} isTextArea={true} />
      <FormInput label="descriptionSQ" name="descriptionSQ" register={register} errors={errors} colSize={12} isTextArea={true} />
      <FormCheckbox label="isActive" register={register} name="isActive" colSize={4} />
      <FormCheckbox label="isVisible" register={register} name="isVisible" colSize={4} />
    </FormLayout>
  );
};

export default observer(Edit);
